import { PagedItems } from '@texas/types';
import { httpClient } from '../httpClient/httpClient';

export interface SlimUser {
  name: string;
  email: string;
  id: number;
  branchIdentifier?: string;
}

export interface User {
  id: number;
  name: string;
  email: string;
  username: string;
  branchId?: number;
  role: Role;
  userType: UserType | null;
  customerId?: number;
  customerName?: string;
  archived: boolean;
  emailNotifications: boolean;
  timeZone: string | null;
  betaFeatures: boolean;
  lastActivity: string | null;
  lastLogin: string | null;
  organizationId: number;
  organization: {
    id: number;
    name: string;
  };
  passwordLoginEnabled: boolean;
  rudholmIdentityLoginEnabled: boolean;
  rudholmIdentityLinked: boolean;
}

export interface UserListItem {
  id: number;
  name: string;
  email: string;
  archived: string | null;
  branch: string | null;
  branchIdentifier: string | null;
  role: Role | null;
  userType: UserType | null;
  customerId?: number;
  customerName?: string;
  articleCount: number;
  customerCount: number;
  lastLogin: string;
  lastActivity: string;
}

export interface UserAssignment {
  id: number;
  branchName: string | null;
  branchIdentifier: string | null;
  userType: UserType | null;
  customUserType: string | null;
}

export interface UserArticleAssignment extends UserAssignment {
  articleId: number;
  articleName: string;
  itemCode: string;
}

export interface UserCustomerAssignment extends UserAssignment {
  customerId: number;
  customerName: string;
  customerIdentifier: string;
}

export interface UserAssignments {
  articles: UserArticleAssignment[];
  customers: UserCustomerAssignment[];
}

export interface UserSearchQuery {
  searchTerm: string;
  includeArchived: boolean;
  sortBy: string;
  sortDesc: boolean;
  branchId: number | null;
  role: Role | null;
  type: UserType | null;
  page: number;
  pageSize: number;
}

export enum Role {
  SystemAdministrator = 1,
  InternalAdministrator = 2,
  InternalAgent = 3,
  Brand = 4,
  Supplier = 5,
}

export enum UserType {
  ProductDeveloper = 1,
  BrandCoordinator = 2,
  KeyAccountManager = 3,
  CustomerService = 4,
  Supplier = 5,
  Brand = 6,
}

export interface UpdateUserRequest {
  name: string;
  email: string;
  branchId?: number;
  customerId?: number;
  providedPassword?: string;
  emailNotifications: boolean;
  userType: UserType | null;
  timeZone: string | null;
  betaFeatures: boolean;
}

export interface UpdateUserRoleRequest {
  role: Role;
  providedPassword: string;
  customerId?: number;
}

export interface CreateUserRequest {
  name: string;
  email: string;
  role: Role;
  userType: UserType | null;
  providedPassword: string | null;
  branchId?: number;
  customerId?: number;
  supplierId?: number;
  emailNotifications: boolean;
  sendWelcomeMail: boolean;
  timeZone: string | null;
  betaFeatures: boolean;
  contactId?: number;
  passwordLoginEnabled: boolean;
  rudholmIdentityLoginEnabled: boolean;
}

export interface SetUserLoginMethodsRequest {
  passwordLoginEnabled: boolean;
  rudholmIdentityLoginEnabled: boolean;
}

export interface InternalUser {
  name: string;
  email: string;
  id: number;
  branchId: number;
  branchName: string;
}

export const userApi = {
  getMe: () => httpClient.get<User>('users/me'),
  getUsers: (roles?: Role[]) =>
    httpClient.get<User[]>('users', { params: { roles: roles } }),
  getAllInternal: () => httpClient.get<User[]>('users/all-internal'),
  archive: (id: number) => httpClient.put<User>(`users/${id}/archive`),
  restore: (id: number) => httpClient.put<User>(`users/${id}/restore`),
  signOutDevices: (id: number) =>
    httpClient.put<User>(`users/${id}/sign-out-devices`),
  sendWelcomeMail: (id: number) => httpClient.put<User>(`users/${id}/welcome`),
  searchUsers: (params: UserSearchQuery) =>
    httpClient.get<PagedItems<UserListItem>>('users/search', { params }),
  getUser: (id: number) => httpClient.get<User>(`users/${id}`),
  getAssignments: (id: number) =>
    httpClient.get<UserAssignments>(`users/${id}/assignments`),
  updateUser: (id: number, request: UpdateUserRequest) =>
    httpClient.put<User>(`users/${id}`, { data: request }),
  updateMe: (request: UpdateUserRequest) =>
    httpClient.put<User>(`users/update-me`, { data: request }),
  createUser: (request: CreateUserRequest) =>
    httpClient.post<SlimUser>('users', { data: request }),
  updateUserRole: (id: number, request: UpdateUserRoleRequest) =>
    httpClient.put<User>(`users/${id}/update-role`, { data: request }),
  setLoginMethods: (id: number, request: SetUserLoginMethodsRequest) =>
    httpClient.put<User>(`users/${id}/login-methods`, { data: request }),
  getInternalUsersWithBranch: () =>
    httpClient.get<InternalUser[]>('users/internal'),
};
